import {
  createNewSchool,
  getDistrictSchools,
  getSchoolDemographic,
  getStatusList,
  updateSchoolBatchType
} from "../api/schools";
import {updateSessionId} from "./session";
import {validateSchoolForm} from "../utils/schools";
import {dispatchError} from "../utils";

const initialState = {
  schoolProfile: null,
  schoolDemographicInfo: null,
  schoolFormErrors: {},
  schoolSaveProfileErrors: {},
  schools: null,
  schoolsLists: [],
  batchTypeChangeErrors: null,
  batchTypeChangeSuccess: null,
  fileStatusesErrors: null,
  schoolStatuses: null,
  addSchoolAlert: null,
  addSchoolErrors: null,
  addSchoolApiErrors: null,
  fetchSchoolsError: null
};

export const fetchSchools = (district) => async (dispatch, getState) => {
  const state = getState();

  const queryParams = {
    loggedInUserName: state.session.currentUser.username,
    sessionId: state.session.sessionData
  }

  let useDistrict = (district) ? district : state.districts.currentDistrict;

  try {
    let schools = [];

    schools = await getDistrictSchools(useDistrict, queryParams);
    await dispatch(updateSessionId(schools[0].loggedInUserSessionId));

    await dispatch({
      type: 'schools/schoolsFetched',
      payload: schools
    });
  } catch (error) {
    await dispatchError(error,{
      type: 'schools/fetchSchoolsFailed',
      payload: error
    }, dispatch);
  }

}

/**
 * The difference between this method and fetchSchools is that fetchSchools
 * is used to retrieve one list of schools for a given district, while this adds the
 * fetched list to an array of lists of schools
 *
 * @param district
 * @param index
 * @returns {(function(*, *): Promise<void>)|*}
 */

export const fetchSchoolsLists = (district, index = 0) => async (dispatch, getState) => {
  const state = getState();

  const schoolsLists = state.schools.schoolsLists;

  const queryParams = {
    loggedInUserName: state.session.currentUser.username,
    sessionId: state.session.sessionData
  }

  try {
    const schools = await getDistrictSchools(district, queryParams);

    schoolsLists[index] = await schools;

    dispatch({
      type: 'schools/schoolsListsFetched',
      payload: schoolsLists
    });
    dispatch(updateSessionId(schools[0].loggedInUserSessionId));
  } catch (error) {
    await dispatchError(error, {
      type: 'schools/schoolsListsFailed',
      payload: error
    }, dispatch);
  }
}

export const buildSchoolLists = (districts) => async (dispatch, getState) => {
  const state = getState();

  const schoolsLists = [];

  const queryParams = {
    loggedInUserName: state.session.currentUser.username,
    sessionId: state.session.sessionData
  }

  let nextSessionId = null;

  try {
    for (const district of districts) {
      const schools = await getDistrictSchools(district, queryParams);
      nextSessionId = schools[0].loggedInUserSessionId;
      queryParams['sessionId'] = nextSessionId;

      schoolsLists.push(schools);
    }

    await dispatch({
      type: 'schools/schoolsListsFetched',
      payload: schoolsLists
    });

    await dispatch(updateSessionId(nextSessionId));
  } catch (error) {
    await dispatchError(error, {
      type: 'schools/schoolsListsFailed',
      payload: error
    }, dispatch);
  }
}

export const schoolsListsRemove = (index) => async (dispatch, getState) => {
  const state = getState();

  const newSchoolsLists = state.schools.schoolsLists.slice();

  newSchoolsLists.splice(parseInt(index), 1);

  dispatch({
    type: 'schools/schoolsListsRemoved',
    payload: newSchoolsLists
  })
}

export const changeBatchTypeStatus = (school, newBatchType) => async (dispatch, getState) => {
  const state = getState();

  const queryParams = {
    loggedInUserName: state.session.currentUser.username,
    sessionId: state.session.sessionData,
  }

  try {
    const batchTypeSchool = {
      atpCode: school.atpCode,
      batchType: newBatchType,
      city: school.city,
      districtId: school.districtId,
      id: school.id,
      name: school.name,
      accessDataStatus: school.accessDataStatus
    }

    const batchTypeChangeResponse = await updateSchoolBatchType(queryParams, [batchTypeSchool]);
    dispatch(updateSessionId(batchTypeChangeResponse.loggedInUserSessionId));
    dispatch({
      type: 'schools/changeBatchTypeApiSuccess'
    });

  } catch (error) {
    await dispatchError(error, {
      type: 'schools/changeBatchTypeApiFailed',
      payload: error
    }, dispatch);
  }
}

export const addSchool = (schoolComposite) => async (dispatch, getState) => {
  const state = getState();

  const queryParams = {
    loggedInUserName: state.session.currentUser.username,
    sessionId: state.session.sessionData,
  }

  const formErrors = validateSchoolForm(schoolComposite.school);

  if (formErrors) {
    dispatch({
      type: 'schools/addSchoolErrors',
      payload: formErrors
    });

    return null;
  }


  try {
    const addSchoolResponse = await createNewSchool(queryParams, schoolComposite);
    dispatch(updateSessionId(addSchoolResponse.loggedInUserSessionId));
    dispatch({
      type: 'schools/addSchoolApiSuccess'
    });

  } catch (error) {
    await dispatchError(error, {
      type: 'schools/addSchoolApiFailed',
      payload: error
    }, dispatch);
  }
}

export const getSchoolDemographicInfo = (atpCode) => async (dispatch, getState) => {
  const state = getState();

  const queryParams = {
    loggedInUserName: state.session.currentUser.username,
    sessionId: state.session.sessionData,
    atpCode: atpCode
  }

  try {
    const schoolDemographicInfo = await getSchoolDemographic(queryParams);
    dispatch(updateSessionId(schoolDemographicInfo.loggedInUserSessionId));
    dispatch({
      type: 'schools/getSchoolDemographicInfoSuccess',
      payload: {
        schoolDemographicInfo: schoolDemographicInfo
      }
    });

  } catch (error) {

    await dispatchError(error, {
      type: 'schools/getSchoolDemographicInfoApiFailed',
      payload: error
    }, dispatch);
  }
}

export const clearChangeBatchTypeStatusErrors = () => {
  return {
    type: 'schools/clearBatchTypeChangeErrors'
  };
}

export const clearSchoolDemographicInfo = () => {
  return {
    type: 'schools/clearSchoolDemographicInfo'
  };
}

export const getFileStatuses = (district) => async (dispatch, getState)=> {
  const state = getState();

  const queryParams = {
    loggedInUserName: state.session.currentUser.username,
    sessionId: state.session.sessionData
  }

  let schoolStatuses = [];

  try {
    let schools = [];
    let schoolStatusList = [];
    let newSessionId = await queryParams.sessionId;

    schools = await getDistrictSchools(district, queryParams);
    newSessionId = await schools[0]['loggedInUserSessionId'];

    if (schools.length >= 1) {
      const queryParamsStatusList = {
        loggedInUserName: queryParams.loggedInUserName,
        sessionId: newSessionId,
        districtId: district.id
      }

      schoolStatusList = await getStatusList(queryParamsStatusList);

      newSessionId = schoolStatusList[0]['loggedInUserSessionId'];

      for (const school of schools) {
        let schoolStatus = {};
        const schoolFiles = await schoolStatusList.find((schoolFile) => schoolFile.schoolId === school.id)
        schoolStatus = {
          school: school,
          fileStatuses: (schoolFiles) ? [schoolFiles] : []
        }

        await schoolStatuses.push(schoolStatus);
      }
    }

    await dispatch(updateSessionId(newSessionId));

    await dispatch({
      type: 'schools/getFileStatusesApiSuccess',
      payload: schoolStatuses
    });

  } catch (error) {
    await dispatchError(error, {
      type: 'schools/getFileStatusesApiFailure',
      payload: 'Api Error with Get School File Statuses'
    }, dispatch);
  }
}

export const clearSchools = () => {
  return {
    type: 'schools/clearSchools'
  };
}

export const clearSchoolsErrors = () => {
  return {
    type: 'schools/clearSchoolsErrors'
  }
}

export const clearSchoolFormErrors = () => {
  return {
    type: 'schools/clearSchoolFormErrors'
  }
}

export const clearSchoolsLists = () => async (dispatch, getState) => {
  dispatch ( {
    type: 'schools/clearSchoolsLists'
  });
}

export const clearAddSchoolAlerts = () => async (dispatch, getState) => {
  dispatch ( {
    type: 'schools/clearAddSchoolAlerts'
  });
}

export const clearFileStatusesErrors = () => {
  return {
    type: 'schools/clearFileStatusesErrors'
  }
}

export default function schoolsReducer(state = initialState, action) {
  const { type, payload } = action;

  switch (type) {
    case 'schools/schoolsFetched':
      return {
        ...state,
        schools: payload
      }
    case 'schools/schoolsListsFetched':
      return {
        ...state,
        schoolsLists: payload
      }
    case 'schools/schoolsListsRemoved':
      return {
        ...state,
        schoolsLists: payload
      }
    case 'schools/fetchSchoolsFailed':
    case 'schools/schoolsListsFailed':
      return {
        ...state,
        fetchSchoolsError: payload
      }
    case 'schools/clearSchoolsError':
      return {
        ...state,
        fetchSchoolsError: null
      }
    case 'schools/changeBatchTypeApiSuccess':
      return {
        ...state,
        batchTypeChangeErrors: null,
        batchTypeChangeSuccess: true
      }
    case 'schools/changeBatchTypeApiFailure':
      return {
        ...state,
        batchTypeChangeErrors: payload,
        batchTypeChangeSuccess: null
      }
    case 'schools/addSchoolErrors':
      return {
        ...state,
        addSchoolApiErrors: null,
        addSchoolErrors: payload,
        addSchoolAlert: null
      }
    case 'schools/addSchoolApiSuccess':
      return {
        ...state,
        addSchoolApiErrors: null,
        addSchoolErrors: null,
        addSchoolAlert: true,
        schools: null
      }
    case 'schools/addSchoolApiFailed':
      return {
        ...state,
        addSchoolErrors: null,
        addSchoolApiErrors: payload,
        addSchoolAlert: null
      }
    case 'schools/clearBatchTypeChangeErrors':
      return {
        ...state,
        batchTypeChangeErrors: null,
        batchTypeChangeSuccess: null
      }
    case 'schools/clearAddSchoolAlerts':
      return {
        ...state,
        addSchoolErrors: null,
        addSchoolApiErrors: null,
        addSchoolAlert: null
      }
    case 'schools/getSchoolDemographicInfoSuccess':
      return {
        ...state,
        schoolDemographicInfo: payload.schoolDemographicInfo,
        getSchoolDemographicInfoFailure: null
      }
    case 'schools/getSchoolDemographicInfoApiFailed':
      return {
        ...state,
        schoolDemographicInfo: null,
        getSchoolDemographicInfoApiFailure: payload
      }
    case 'schools/getFileStatusesApiSuccess':
      return {
        ...state,
        fileStatusesErrors: null,
        schoolStatuses: payload
      }
    case 'schools/getFileStatusesApiFailure':
      return {
        ...state,
        fileStatusesErrors: payload,
        schoolStatuses: []
      }
    case 'schools/clearFileStatusesErrors':
      return {
        ...state,
        fileStatusesErrors: null
      }
    case 'schools/clearSchoolDemographicInfo':
      return {
        ...state,
        schoolDemographicInfo: null,
        getSchoolDemographicInfoApiFailure: null
      }
    case 'schools/clearSchools':
      return {
        ...state,
        schools: null,
        schoolStatuses: null
      }
    case 'schools/clearSchoolsLists':
      return {
        ...state,
        schoolsLists: []
      }
    default:
      return {
        ...state
      }
  }
}